import React, { useEffect, useState } from 'react'
import { DashboardModal } from '@uppy/react'
import AwsS3, { type AwsBody } from '@uppy/aws-s3'
import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import Uppy from '@uppy/core'
import Button from '../../views/components/atoms/Button/Button'
import styles from './UppyWrapper.module.scss'

import * as Requests from './Requests'
import {
  leaseFileTypes,
  naturalPersonDocumentTypes,
  entityDocumentTypes,
  invoiceFileTypes,
  defaultDocumentTypes,
  propertyFileTypes,
} from './constants'
import { $TSFixMe } from 'types/ts-migrate'

type TProps = {
  parentProps: {
    authToken: string
    documentServiceEndpoint: string
  }
  forOwner?: {
    className?: string
    classId?: string
  }
  partyType?: 'person' | 'company' | false
  onComplete: (files: any) => void
}

const UppyWrapper = (props: TProps) => {
  const {
    parentProps: { authToken, documentServiceEndpoint },
    forOwner,
    partyType,
    onComplete,
  } = props

  type Meta = { name: string | undefined; type: string | undefined }

  // Function to get appropriate file types based on forOwner and partyType
  const getFileTypes = () => {
    if (forOwner?.className === 'invoice') {
      return invoiceFileTypes
    } else if (forOwner?.className === 'lease') {
      return leaseFileTypes
    } else if (forOwner?.className === 'property') {
      return propertyFileTypes
    } else if (forOwner?.className === 'party') {
      if (partyType === 'person') {
        return naturalPersonDocumentTypes
      } else if (partyType === 'company') {
        return entityDocumentTypes
      } else {
        // Default to natural person if partyType is not specified or unknown
        return naturalPersonDocumentTypes
      }
    } else {
      // Default to lease file types if forOwner.className is not recognized
      return defaultDocumentTypes
    }
  }

  const uppyReference = React.useRef(
    new Uppy<Meta, AwsBody>({
      meta: { name: '', type: '' },
      restrictions: {
        requiredMetaFields: ['name', 'type'],
      },
    }),
  )

  // Refresh Uppy instance when forOwner changes
  useEffect(() => {
    uppyReference.current = new Uppy<Meta, AwsBody>({
      meta: { name: '', type: '' },
      restrictions: {
        requiredMetaFields: ['name', 'type'],
      },
    })
  }, [forOwner])

  const [isOpen, setIsOpen] = useState(false)

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const __setIsOpen = (b: any) => {
    // uppyReference.current.open = true
    setIsOpen(b)
  }

  useEffect(() => {
    const parentProps = { authToken, documentServiceEndpoint, forOwner }
    const current = uppyReference.current
    if (current.getPlugin('AwsS3Multipart') == null) {
      current.use(AwsS3, {
        shouldUseMultipart() {
          return true
        },
        getUploadParameters: Requests.getUploadParameters.bind(null, parentProps),
        createMultipartUpload: Requests.createMultipartUpload.bind(null, parentProps),
        listParts: Requests.listParts.bind(null, parentProps),
        signPart: Requests.signPart.bind(null, parentProps),
        abortMultipartUpload: Requests.abortMultipartUpload.bind(null, parentProps),
        completeMultipartUpload: Requests.completeMultipartUpload.bind(null, parentProps),
      })
      current.on('complete', files => {
        // hide upload window if no failures
        if (files.failed?.length === 0 && Array.isArray(files.successful) && files.successful.length > 0) {
          setIsOpen(false)
          current.clear()
          onComplete(files)
        }
      })
      // Reset file type meta to empty string when file is added
      current.on('file-added', file => {
        current.setFileMeta(file.id, {
          ...file.meta,
          type: '', // Reset type to empty string instead of MIME type
        })
      })
    }
  }, [authToken, documentServiceEndpoint, onComplete, forOwner, uppyReference])

  useEffect(() => {
    const current = uppyReference.current
    return () => current.destroy()
  }, [uppyReference])

  return (
    <div className={styles.root}>
      <Button size="sm" onClick={() => __setIsOpen(true)}>
        Upload
      </Button>
      <DashboardModal
        target={'body'}
        uppy={uppyReference.current}
        closeModalOnClickOutside
        open={isOpen}
        onRequestClose={() => __setIsOpen(false)}
        plugins={[]}
        metaFields={[
          { id: 'name', name: 'Name', placeholder: 'File name' },
          {
            id: 'type',
            name: 'Type',
            render: ({ value, onChange, required, form }, h) => {
              const fileTypes = getFileTypes()
              return h(
                'select',
                {
                  className: 'uppy-Dashboard-Form-input',
                  required,
                  form,
                  onChange: (e: $TSFixMe) => onChange(e.target.value),
                  defaultSelected: '',
                  selected: value,
                  value,
                },
                h('option', { value: '' }, '--Select Type--'),
                fileTypes.map(({ value, label }) => h('option', { value }, label)),
              )
            },
          },
        ]}
        note="Please note: All files uploaded need to have a type selected before you can proceed."
        proudlyDisplayPoweredByUppy={false}
        autoOpen={'metaEditor'}
      />
    </div>
  )
}

export default UppyWrapper
